<template>
  <!-- Two drawers in one, switched at `mobileBreakpoint` (768px —
       plugins/vuetify.ts):
        - DESKTOP: permanent, optionally collapsed to the 56px icon rail.
        - MOBILE:  temporary. It overlays the content with a scrim instead of
          eating 230 of ~390 available pixels, opens from the TopBar's
          hamburger, and closes on a scrim tap, an Escape, or a navigation.
       `rail` is force-disabled on mobile (see the computed): an icon rail
       trades labels for screen width, which a temporary overlay does not
       need, and it would leave a permanent 56px stripe over the content. -->
  <v-navigation-drawer
    :model-value="mobile ? navOpen : true"
    :permanent="!mobile"
    :temporary="mobile"
    :rail="rail"
    :width="mobile ? 272 : 230"
    rail-width="56"
    :border="0"
    class="standalone-nav"
    :class="{ 'standalone-nav--mobile': mobile }"
    @update:model-value="onDrawerModel"
  >
    <!-- The brand block lives in the TopBar, at the far left of the bar above
         this drawer — one identity, one place. This drawer carries
         destinations only. -->
    <div class="sh-nav" :class="{ 'sh-nav--rail': rail }">
      <!-- ── 🔴 WHICH COMPANY YOU ARE ACTING FOR — first, and above everything ──
           Metaship's shape (D37, D41, D42): the organisation at the top of the
           sidebar, with "Change organisation" under it. A person on a
           collaborative platform is always acting FOR somebody, and the answer
           belongs where their eye already is.

           🔴 It stays put INSIDE a production, where the drawer below it is
           replaced wholesale. The production is one screen's context; the
           organisation is the whole session's, and a control that vanished on
           the way into a show would be saying the opposite. -->
      <OrganisationSwitcher
        :choices="organisationChoices"
        :chosen="chosenOrganisation"
        :ready="organisationReady"
        :rail="rail"
        :agencies="representingAgencies"
        @choose="onChooseOrganisation"
      />

      <!-- ── 🔴 INSIDE A PRODUCTION, THIS DRAWER IS THAT PRODUCTION ────────
           It does not gain a second section below the app's own: it is
           REPLACED. Two things come before the destinations, in this order,
           and the order is the point:

             1. the way OUT, as a distinct boxed control. A person who has
                walked into a context must be able to see how to leave it
                before they have read anything else in it.
             2. the production's IDENTITY — its icon, its name, and a line
                saying what it is (kind · territory · dates). Never its uuid:
                a producer recognises "Wilder Hearts", and has never
                recognised 5f2c1f9a-0d0e-….

           Only then that production's own nav (`nav/navSections.ts`). -->
      <!-- 🔴 Not on a phone: the top bar already names the production and holds the way out in its
           menu (Devin, 2026-09-25: "remove the All productions in mobile mode, it's already at the top"). -->
      <template v-if="inProduction && !mobile">
        <button
          v-if="wayOut"
          type="button"
          class="sh-out"
          :title="rail ? t('nav.allProductions') : undefined"
          @click="onLeaveProduction"
        >
          <v-icon size="15" class="sh-out__icon">arrow_back</v-icon>
          <span v-if="!rail" class="sh-out__label">{{
            t("nav.allProductions")
          }}</span>
        </button>

        <div
          class="sh-context"
          :title="contextTitle"
          data-testid="production-context"
        >
          <span class="sh-context__icon"
            ><v-icon size="17">{{ contextIcon }}</v-icon></span
          >
          <span v-if="!rail" class="sh-context__id">
            <span class="sh-context__name">{{ contextName }}</span>
            <span v-if="contextSubtitle" class="sh-context__sub">{{
              contextSubtitle
            }}</span>
          </span>
        </div>
      </template>

      <!-- ── 🔴 INSIDE A MODULE, THIS DRAWER IS THAT MODULE (D51) ────────
           The same two things, in the same order, for the same reasons as a
           production above: the way OUT first, then the module's IDENTITY.
           Only the label differs — you leave a production for the slate, and
           a module for all the others. -->
      <template v-else-if="inModule">
        <button
          v-if="moduleWayOut"
          type="button"
          class="sh-out"
          :title="rail ? t('nav.allModules') : undefined"
          data-testid="module-way-out"
          @click="onLeaveModule"
        >
          <v-icon size="15" class="sh-out__icon">arrow_back</v-icon>
          <span v-if="!rail" class="sh-out__label">{{
            t("nav.allModules")
          }}</span>
        </button>

        <div
          class="sh-context"
          :title="inModule.name"
          data-testid="module-context"
        >
          <span class="sh-context__icon"
            ><v-icon size="17">{{ inModule.icon || "apps" }}</v-icon></span
          >
          <span v-if="!rail" class="sh-context__id">
            <span class="sh-context__name">{{ inModule.name }}</span>
          </span>
        </div>
      </template>

      <div ref="scrollEl" class="sh-scroll">
        <!-- The one active-row marker for the whole nav. It is not drawn per
             row: a single element that SLIDES to the lit row (top/height
             transition) reads as one thing moving, where per-row bars would
             blink off and on. Measured, not laid out — see measureIndicator(). -->
        <div
          v-show="indicator"
          class="sh-indicator"
          :style="indicatorStyle"
          aria-hidden="true"
        ></div>
        <!-- ── One module ⇒ the app IS that module ────────────────────────
             A tenant app with a single module has nothing to choose between:
             a parent row that expands into the only other thing on screen is
             pure ceremony, and Home is a second destination competing with
             it. So the module's sections become the sidebar itself, drawn at
             top-level metrics — identical to how the Metaship sidebar this was forked from
             draws a DRILLED module (`.sv2-item`: 30px rows, 13px labels, no
             child indent), not as the 12px accordion children below. -->
        <!-- 🔴 A DRILLED module's own sections (D51), at the same drilled
             metrics as the sole-module case above and for the same reason:
             inside Admin, these ARE the sidebar. The way out is the boxed
             control above them, and there is exactly one. -->
        <template v-if="inModule">
          <template
            v-for="group in moduleGroups"
            :key="'mod-' + (group.label || '_')"
          >
            <div v-if="group.label && !rail" class="sh-section">
              {{ group.label }}
            </div>
            <div v-else-if="group.label && rail" class="sh-section__rule"></div>
            <div
              v-for="(sub, index) in group.items"
              :key="sub.route"
              class="sh-item sh-item--in"
              :class="{ 'sh-item--active': isSubActive(sub.route) }"
              :style="{ '--i': index }"
              :title="rail ? sub.name : undefined"
              data-testid="module-section"
              @click="onSubClick(sub)"
            >
              <span class="sh-item__icon">
                <v-icon :size="sub.icon ? 17 : 6">{{
                  sub.icon || "fiber_manual_record"
                }}</v-icon>
              </span>
              <span v-if="!rail" class="sh-item__label">{{ sub.name }}</span>
            </div>
          </template>
        </template>

        <template v-else-if="soleModule">
          <template
            v-for="group in groupSubItems(soleModule)"
            :key="'sole-' + (group.label || '_')"
          >
            <div v-if="group.label && !rail" class="sh-section">
              {{ group.label }}
            </div>
            <div v-else-if="group.label && rail" class="sh-section__rule"></div>
            <div
              v-for="(sub, index) in group.items"
              :key="sub.route"
              class="sh-item sh-item--in"
              :class="{ 'sh-item--active': isSubActive(sub.route) }"
              :style="{ '--i': index }"
              :title="rail ? sub.name : undefined"
              @click="onSubClick(sub)"
            >
              <span class="sh-item__icon">
                <v-icon :size="sub.icon ? 17 : 6">{{
                  sub.icon || "fiber_manual_record"
                }}</v-icon>
              </span>
              <span v-if="!rail" class="sh-item__label">{{ sub.name }}</span>
            </div>
          </template>
        </template>

        <template v-else>
          <!-- 🔴 D157: what the caller is on this production is still in flight. A quiet skeleton, not
               the full menu — the rail fails closed, so a financier is never shown the budget's rows
               for a moment before they vanish. -->
          <div
            v-if="standingLoading"
            class="sh-skeleton"
            aria-busy="true"
            :aria-label="t('nav.loadingProduction')"
            data-testid="production-nav-skeleton"
          >
            <div
              v-for="row in 5"
              :key="row"
              class="sh-skeleton__row"
              :style="{ '--row': row }"
            ></div>
          </div>
          <div
            v-else-if="nothingOpen && !rail"
            class="sh-nothing"
            data-testid="production-nav-empty"
          >
            {{ t("nav.nothingOpen") }}
          </div>

          <div
            v-if="homeEnabled"
            class="sh-item"
            :class="{ 'sh-item--active': isActive('/') }"
            :title="rail ? t('nav.home') : undefined"
            @click="onHomeClick"
          >
            <span class="sh-item__icon"><v-icon size="17">home</v-icon></span>
            <span v-if="!rail" class="sh-item__label">{{ t("nav.home") }}</span>
          </div>

          <!-- ── The nav, in GROUPS ────────────────────────────────────
               A flat run of ten rows is a list; the same ten under
               WORKSPACE / DIRECTORY / SETTINGS is a structure a person can
               scan. The headers are the TENANT's (`section` on its own
               tiles), so nothing here decides what belongs together.

               Rows stay DIRECT children of `.sh-scroll` — the sliding active
               marker is measured with `offsetTop` against it, and a wrapper
               per group would reset every measurement to zero. -->
          <template
            v-for="navGroup in navGroups"
            :key="'g-' + (navGroup.label || '_')"
          >
            <div v-if="navGroup.label && !rail" class="sh-section">
              {{ navGroup.label }}
            </div>
            <div
              v-else-if="navGroup.label && rail"
              class="sh-section__rule"
            ></div>

            <template v-for="item in navGroup.items" :key="item.route">
              <!-- Rail: the row is a hover flyout activator, so a collapsed sidebar
                 still reaches a module's sections (SidebarV2's rail contract). -->
              <v-menu
                v-if="rail && hasItems(item)"
                open-on-hover
                location="right"
                transition="slide-x-transition"
                :open-delay="50"
                :close-delay="60"
              >
                <template #activator="{ props }">
                  <div
                    class="sh-item"
                    :class="{ 'sh-item--active': isActive(item.route) }"
                    v-bind="props"
                    @click="onItemClick(item)"
                  >
                    <span class="sh-item__icon">
                      <v-icon size="17">{{ item.icon || "circle" }}</v-icon>
                    </span>
                    <v-icon size="11" class="sh-item__more"
                      >chevron_right</v-icon
                    >
                  </div>
                </template>
                <div class="sh-flyout">
                  <div class="sh-flyout__title">{{ item.name }}</div>
                  <div
                    v-for="(sub, index) in item.items"
                    :key="sub.route"
                    class="sh-child"
                    :class="{ 'sh-child--active': isSubActive(sub.route) }"
                    :style="{ '--i': index }"
                    @click="onSubClick(sub)"
                  >
                    <v-icon :size="sub.icon ? 13 : 6" class="sh-child__bullet">
                      {{ sub.icon || "fiber_manual_record" }}
                    </v-icon>
                    <span class="sh-child__label">{{ sub.name }}</span>
                  </div>
                </div>
              </v-menu>

              <div
                v-else
                class="sh-item"
                :class="{ 'sh-item--active': isActive(item.route) }"
                :title="rail ? item.name : undefined"
                @click="onItemClick(item)"
              >
                <span class="sh-item__icon">
                  <v-icon size="17">{{ item.icon || "circle" }}</v-icon>
                </span>
                <span v-if="!rail" class="sh-item__label">{{ item.name }}</span>
                <v-icon
                  v-if="!rail && hasItems(item)"
                  size="14"
                  class="sh-item__chev"
                  :class="{ 'sh-item__chev--open': expanded === item.route }"
                  >expand_more</v-icon
                >
              </div>

              <!-- Expanded sub-sections. Grouped by the sub-item's own `section`
                 eyebrow, exactly as SidebarV2 renders a drilled module. -->
              <!-- Height is animated by the JS hooks (auto → px → auto): CSS
                 cannot transition to `height: auto`, and a section list that
                 pops open in one frame is what makes a nav feel plain. -->
              <Transition
                name="sh-expand"
                @enter="onExpandEnter"
                @after-enter="onExpandDone"
                @leave="onExpandLeave"
              >
                <div
                  v-if="!rail && hasItems(item) && expanded === item.route"
                  class="sh-children"
                >
                  <template
                    v-for="group in groupSubItems(item)"
                    :key="item.route + '-' + (group.label || '_')"
                  >
                    <div v-if="group.label" class="sh-children__label">
                      {{ group.label }}
                    </div>
                    <div
                      v-for="(sub, index) in group.items"
                      :key="sub.route"
                      class="sh-child"
                      :class="{ 'sh-child--active': isSubActive(sub.route) }"
                      :style="{ '--i': index }"
                      @click="onSubClick(sub)"
                    >
                      <v-icon
                        :size="sub.icon ? 14 : 6"
                        class="sh-child__bullet"
                      >
                        {{ sub.icon || "fiber_manual_record" }}
                      </v-icon>
                      <span class="sh-child__label">{{ sub.name }}</span>
                    </div>
                  </template>
                </div>
              </Transition>
            </template>
          </template>
        </template>
      </div>

      <!-- Organisation · user · language · theme — pinned under the last
           destination. Lives in its own component so this file stays about
           destinations. -->
      <SidebarFooter :rail="rail" />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import { navigateToUrl } from "single-spa";
import { reloadMountedModules } from "@/spa/registry";
import { useSessionStore } from "@/stores/session";
import { useProductionStore } from "@/stores/production";
import { useOrganisationStore } from "@/stores/organisation";
import { useScopeStore } from "@/stores/scopes";
import { filterNavByScopes, filterNavForNoCompany } from "@/nav/navScopes";
import { filterNavByFunctions } from "@/nav/navFunctions";
import { filterProductionNavByStanding } from "@/nav/navStanding";
import {
  appScopedItems,
  drilledModule,
  insideProduction,
  navForApp,
  navForProduction,
  productionScopedItems,
  wayOutOfProduction,
  type NavGroup,
} from "@/nav/navSections";
import {
  formatShortDate,
  productionHeading,
  productionIcon,
  productionSubtitle,
} from "@/production/identity";
import { closeNav, navOpen, rail as railPreference } from "./navState";
import OrganisationSwitcher from "./OrganisationSwitcher.vue";
import SidebarFooter from "./SidebarFooter.vue";
import type { NavBarItem, NavBarSubItem } from "@/app/types";
import { APP_HOME_PAGE, APP_HOME_PATH } from "@/app";

interface SubItemGroup {
  label: string | null;
  items: NavBarSubItem[];
}

/** Accordion open/close duration — keep in step with `.sh-expand-*-active` below. */
const EXPAND_MS = 260;

export default defineComponent({
  name: "Sidebar",
  components: { OrganisationSwitcher, SidebarFooter },
  setup() {
    const session = useSessionStore();
    const production = useProductionStore();
    const organisation = useOrganisationStore();
    const scopes = useScopeStore();
    const { t, locale } = useI18n();
    // `mobileBreakpoint: 768` — plugins/vuetify.ts.
    const { mobile } = useDisplay();
    const path = ref(window.location.pathname + window.location.search);

    /** The icon rail is a desktop-only affordance — see the template comment. */
    const rail = computed(() => !mobile.value && railPreference.value);

    /**
     * Vuetify writes `false` back through here when a temporary drawer is
     * dismissed by its scrim, by Escape, or by a swipe. Without it the drawer
     * would reopen on the next render, because `model-value` is a one-way
     * binding off `navOpen`.
     */
    function onDrawerModel(open: boolean): void {
      if (mobile.value) navOpen.value = open;
    }

    /**
     * 🔴 **A NAMED HANDLER, SO IT CAN BE TAKEN OFF AGAIN.**
     *
     * These two were registered with inline arrow functions, so there was no reference to hand
     * `removeEventListener` and `onBeforeUnmount` cleared only the expand timer. The Sidebar
     * unmounts on sign-out and mounts again on the next sign-in, so a browser left open through a
     * few sign-in cycles accumulated a pair each time — every one of them holding this component's
     * whole closure alive, and every one of them still writing to a `path` ref nothing renders.
     * Found by a sweep on 2026-09-12.
     */
    const followLocation = (): void => {
      path.value = window.location.pathname + window.location.search;
    };
    window.addEventListener("single-spa:before-routing-event", followLocation);
    window.addEventListener("popstate", followLocation);

    /**
     * 🔴 The entries this person may actually REACH (D54).
     *
     * The tenant says what the app contains; this drops what the gate would refuse, and then what
     * this company has no trade for. Found by the persona picker: a member's rail offered Admin.
     * Nothing is hidden until the scopes are known, so a boot draws the full list once and settles
     * rather than emptying and refilling — see `navScopes.ts` for why uncertainty fails OPEN.
     *
     * 🔴 Two filters, in this order, because they answer two different questions (D91): what this
     * PERSON may open, then what this COMPANY has any use for. `navFunctions.ts` carries why the
     * second one treats an empty answer as an answer where the first does not.
     */
    /** In no company at all — the list answered, and nothing in it to choose (D157). */
    const inNoCompany = computed(
      () => organisation.ready && organisation.selectable.length === 0,
    );
    const appNavItems = computed(() =>
      inNoCompany.value
        ? filterNavForNoCompany(appScopedItems(session.navItems))
        : filterNavByFunctions(
            filterNavByScopes(appScopedItems(session.navItems), scopes.held),
            {
              functions: organisation.chosen?.functions ?? [],
              // 🔴 Known once the switcher's list has been ANSWERED, however empty. Before that nothing is
              // hidden, for the same reason the scopes filter waits: a rail that loses a row on every boot
              // and grows it back reads as a bug (D91).
              known: organisation.ready && organisation.chosenId !== null,
            },
          ),
    );
    /**
     * 🔴 A PRODUCTION's entries are drawn from what the caller IS on it (D157, `nav/navStanding.ts`),
     * and they FAIL CLOSED: until `GET /productions/:id/me` has answered there are none. The company
     * scopes still have a say only where a production role does not reach (deals, time, the directory).
     */
    const productionNavItems = computed(() =>
      filterProductionNavByStanding(
        productionScopedItems(session.navItems),
        production.standing,
        scopes.held,
      ),
    );
    const navItems = computed<NavBarItem[]>(() => [
      ...appNavItems.value,
      ...productionNavItems.value,
    ]);

    /**
     * 🔴 WHICH SIDEBAR. Inside a production this drawer is that production's and
     * nothing else; outside, it is the app's own destinations. Both answers come
     * from `nav/navSections.ts` — the shell holds no opinion about which entries
     * belong to a production, only about how the two states are drawn.
     */
    // 🔴 Read off the UNFILTERED list (D157): whether this drawer is a production's is a fact about the
    // URL and the manifest. Asked of the filtered list, a rail that fails closed while the standing is
    // in flight — or a stranger's empty one — would flip the drawer back to the app's nav.
    const inProduction = computed(() =>
      insideProduction(session.navItems, production.id),
    );
    /** Inside a production, and what the caller is on it is not known yet: a skeleton, never the full menu. */
    const standingLoading = computed(
      () => inProduction.value && production.standing === null,
    );
    /** Known, and nothing on this production is open to them — say so quietly rather than draw a blank. */
    const nothingOpen = computed(
      () =>
        inProduction.value &&
        !standingLoading.value &&
        productionNavItems.value.length === 0,
    );
    /**
     * 🔴 The MODULE this drawer has become (D51) — Admin, today. A module that names its own sections
     * is a place, and being on one of its routes puts you inside it, exactly as a production does.
     * Never both: inside a production the sidebar is already that production's, and two stacked
     * replacements would leave two ways out and no way to tell which one you are leaving.
     */
    const inModule = computed<NavBarItem | null>(() =>
      inProduction.value ? null : drilledModule(navItems.value, path.value),
    );
    const navGroups = computed<NavGroup[]>(() =>
      inProduction.value && production.id !== null
        ? navForProduction(navItems.value, production.id)
        : navForApp(navItems.value),
    );
    /** A drilled module's rail: its own sections, grouped by the labels the tenant wrote. */
    const moduleGroups = computed<SubItemGroup[]>(() =>
      groupSubItems(inModule.value),
    );
    /** The rows actually on screen — what "the only module" and "the module you are inside" are read from. */
    const visibleItems = computed<NavBarItem[]>(() =>
      navGroups.value.flatMap((group) => group.items),
    );

    // ── The production this drawer has become ─────────────────────────
    const wayOut = computed(() =>
      wayOutOfProduction(appScopedItems(navItems.value), APP_HOME_PATH),
    );
    /** Never the uuid. Until the title lands, the drawer says what it honestly knows: you are in a production. */
    /**
     * 🔴 The SHOW, not the node (D143). Inside "Season 1" the drawer says "Tuiskoms" and the line
     * under it says which season — a node whose own title is "Season 1" identifies nothing on its own.
     */
    const contextName = computed(() => {
      const identity = production.identity;
      return (
        (identity === null ? null : productionHeading(identity)) ??
        production.title ??
        t("nav.thisProduction")
      );
    });
    const contextIcon = computed(() =>
      productionIcon(production.identity?.kind ?? null),
    );
    const contextSubtitle = computed(() => {
      const identity = production.identity;
      // `locale` is read so the date re-renders when the visitor changes language.
      return identity === null
        ? null
        : productionSubtitle(identity, (iso) =>
            formatShortDate(iso, locale.value),
          );
    });
    /**
     * The whole identity as one hover string. In the rail there is no room for any of it; expanded,
     * the subtitle clamps at two lines and a long name still ellipsizes, so the tooltip is where the
     * unabbreviated answer lives either way.
     */
    const contextTitle = computed(() =>
      contextSubtitle.value === null
        ? contextName.value
        : `${contextName.value} — ${contextSubtitle.value}`,
    );

    function hasItems(item: NavBarItem | null): boolean {
      return !!(item && item.items && item.items.length);
    }

    /**
     * The app's ONE module, when it has exactly one and that module has
     * sections. Its sections replace the whole nav: no parent row, and no
     * Home tab above them (see the template).
     */
    const soleModule = computed<NavBarItem | null>(() => {
      const items = visibleItems.value;
      const only = items.length === 1 ? (items[0] as NavBarItem) : null;
      return hasItems(only) ? only : null;
    });

    /**
     * Home is the app's front door, so it belongs to the app's sidebar. Inside a production it would
     * be a row that silently leaves the context — the "← ALL PRODUCTIONS" control above is the way
     * out, and there is exactly one of those.
     */
    const homeEnabled = computed(
      () => APP_HOME_PAGE.enabled && !soleModule.value && !inProduction.value,
    );

    /**
     * A route WITH a `?section=` (a specific sub-item, or a module collapsed
     * to a single allowed item) is active only on an EXACT path+section
     * match — otherwise every sub-item under the same bare path lit up
     * together the instant the user was anywhere in the module. A route
     * with NO section (a parent/default row) keeps matching its own path
     * plus any nested sub-path.
     *
     * 🔴 …UNLESS a SIBLING row names the section the visitor is on. Then the bare row is only the
     * module's default section, and the sibling owns the highlight. Without this, People (`/people`)
     * and Directory (`/people?section=directory`) lit up together (2026-09-19). A parent like
     * Management keeps its loose match, because no row beside it names its sections.
     */
    function isActive(route: string): boolean {
      const [routePath, routeQuery] = route.split("?");
      const routeSection = new URLSearchParams(routeQuery || "").get("section");
      const [currentPath, currentQuery] = path.value.split("?");
      const currentSection = new URLSearchParams(currentQuery || "").get(
        "section",
      );

      if (routeSection != null) {
        return currentPath === routePath && currentSection === routeSection;
      }
      const onPath =
        currentPath === routePath ||
        (currentPath ?? "").startsWith(`${routePath}/`);
      if (!onPath || currentSection == null) return onPath;
      return !visibleItems.value.some((item) => {
        const [itemPath, itemQuery] = item.route.split("?");
        return (
          item.route !== route &&
          itemPath === routePath &&
          new URLSearchParams(itemQuery || "").get("section") === currentSection
        );
      });
    }

    /**
     * A SUB-item matches on path AND section, with "no section" being a value
     * of its own — the module's default section (e.g. CRM's "Overview", route
     * `/crm`) must not stay lit while the visitor is on `/crm?section=deals`.
     * `isActive` deliberately keeps the loose prefix match for the PARENT row,
     * which represents the whole module. Same rule as
     * `nav/routeGuard.ts:decideRouteGuard`.
     */
    function isSubActive(route: string): boolean {
      const [routePath, routeQuery] = route.split("?");
      const [currentPath, currentQuery] = path.value.split("?");
      return (
        routePath === currentPath &&
        new URLSearchParams(routeQuery || "").get("section") ===
          new URLSearchParams(currentQuery || "").get("section")
      );
    }

    /** The module the visitor is currently inside, if it has sections to show. */
    const activeParentRoute = computed<string | null>(() => {
      const hit = visibleItems.value.find(
        (item) =>
          hasItems(item) && isActive(item.route.split("?")[0] as string),
      );
      return hit ? hit.route : null;
    });

    /**
     * `undefined` = follow the route (the module you are IN shows its sections
     * without a click — a tenant app has a handful of modules, so a sidebar
     * that hides every destination behind an accordion is mostly empty space).
     * A string/null = the visitor overrode that by clicking a parent row; the
     * override is dropped again on the next navigation.
     */
    const manualExpanded = ref<string | null | undefined>(undefined);
    const expanded = computed(() =>
      manualExpanded.value === undefined
        ? activeParentRoute.value
        : manualExpanded.value,
    );
    watch(path, () => {
      manualExpanded.value = undefined;
    });

    /** Consecutive sub-items sharing a `section` become one eyebrow-labelled group. */
    function groupSubItems(item: NavBarItem | null): SubItemGroup[] {
      const groups: SubItemGroup[] = [];
      for (const sub of item?.items || []) {
        const label = sub.section || null;
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(sub);
        else groups.push({ label, items: [sub] });
      }
      return groups;
    }

    // ── Sliding active marker ─────────────────────────────────────────
    // The lit row is found in the DOM after each render rather than derived
    // from the route: the same route may light a top-level row, an accordion
    // child, or a promoted sole-module row, and only the DOM knows where that
    // row ended up once the accordion has opened. A child wins over its
    // parent — the marker sits on the deepest lit row.
    const scrollEl = ref<HTMLElement | null>(null);
    const indicator = ref<{ top: number; height: number } | null>(null);
    const indicatorStyle = computed(() =>
      indicator.value
        ? {
            top: `${indicator.value.top}px`,
            height: `${indicator.value.height}px`,
          }
        : undefined,
    );

    function measureIndicator(): void {
      const root = scrollEl.value;
      if (!root) return;
      const lit =
        root.querySelector<HTMLElement>(".sh-child--active") ??
        root.querySelector<HTMLElement>(".sh-item--active");
      indicator.value = lit
        ? { top: lit.offsetTop, height: lit.offsetHeight }
        : null;
    }
    watch(
      [path, expanded, rail, navGroups, mobile],
      () => void nextTick(measureIndicator),
      { flush: "post" },
    );
    // The accordion's height animation moves every row below it while it
    // runs — keep the marker glued to its row through the whole motion.
    let expandTimer: ReturnType<typeof setInterval> | null = null;
    function trackDuringExpand(): void {
      if (expandTimer) clearInterval(expandTimer);
      const startedAt = Date.now();
      expandTimer = setInterval(() => {
        measureIndicator();
        if (Date.now() - startedAt > EXPAND_MS + 40 && expandTimer) {
          clearInterval(expandTimer);
          expandTimer = null;
        }
      }, 16);
    }
    onMounted(() => void nextTick(measureIndicator));
    onBeforeUnmount(() => {
      if (expandTimer) clearInterval(expandTimer);
      window.removeEventListener(
        "single-spa:before-routing-event",
        followLocation,
      );
      window.removeEventListener("popstate", followLocation);
    });

    // ── Accordion height (auto → px → auto) ───────────────────────────
    function onExpandEnter(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = "0px";
      node.style.overflow = "hidden";
      void node.offsetHeight; // commit the 0 before the target height, or nothing transitions
      node.style.height = `${node.scrollHeight}px`;
      trackDuringExpand();
    }
    function onExpandDone(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = "";
      node.style.overflow = "";
      measureIndicator();
    }
    function onExpandLeave(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = `${node.scrollHeight}px`;
      node.style.overflow = "hidden";
      void node.offsetHeight;
      node.style.height = "0px";
      trackDuringExpand();
    }

    function onHomeClick(): void {
      navigateToUrl("/");
      closeNav();
    }

    /**
     * "← ALL PRODUCTIONS". The route already has the context stripped
     * (`wayOutOfProduction`), so leaving is a plain navigation — the production
     * store notices the URL no longer names one and the drawer becomes the app's
     * again. Nothing here clears any state by hand; the URL is the context.
     */
    function onLeaveProduction(): void {
      if (wayOut.value === null) return;
      navigateToUrl(wayOut.value);
      closeNav();
    }

    /**
     * 🔴 The way out of a MODULE (D51) — all the others.
     *
     * The same destination the way out of a production uses, and deliberately so: `homePath` is the
     * tenant's own answer to "where does a person start", and leaving Admin puts you back at the top
     * of the app rather than at whichever row happens to be first.
     */
    const moduleWayOut = computed(() =>
      wayOutOfProduction(appScopedItems(navItems.value), APP_HOME_PATH),
    );

    function onLeaveModule(): void {
      if (moduleWayOut.value === null) return;
      navigateToUrl(moduleWayOut.value);
      closeNav();
    }

    function onItemClick(item: NavBarItem): void {
      if (hasItems(item)) {
        manualExpanded.value =
          expanded.value === item.route ? null : item.route;
        // On mobile a navigation closes the drawer, so a parent row that both
        // navigated AND closed would make its own sections unreachable — the
        // sections would flash and vanish with the drawer. There, the parent
        // row expands and nothing else; the sub-item does the travelling.
        if (mobile.value) return;
      }
      navigateToUrl(item.route);
      closeNav();
    }

    /**
     * 🔴 Switching company is a CLIENT-SIDE change of a header, and then a reload of what is on screen.
     *
     * No re-authentication and no new token — identity is stable, the organisation is a choice
     * (`auth/http.ts`). But every list on screen was fetched under the OLD header and is now somebody
     * else's data still being drawn, so the screens have to be told.
     *
     * 🔴 **THIS USED TO DISPATCH AN EVENT NOTHING LISTENED TO.** `pinksy:organisation-changed` was
     * fired here and handled nowhere — every module listens for `pinksy:navigate` and nothing else —
     * so the Desk went on drawing the previous company's data under the new company's name.
     * Reproduced in Chrome: switching from a production company to an agency left the production
     * company's show on screen while the rail had already become the agency's.
     *
     * Firing `pinksy:navigate` instead would not have been enough either: a module's handler only
     * refetches when the PRODUCTION or the version in the URL changed, and switching company changes
     * neither. `reloadMountedModules()` unloads what is mounted so it is holding nothing, and
     * single-spa mounts it straight back — with the new header (`spa/registry.ts` argues it).
     *
     * A production goes with it. A show belongs to the company making it, so carrying the
     * `?production=` of one company into another is at best a 404 and at worst a question the gate has
     * to answer instead of the UI. Leaving is a plain NAVIGATION to the way-out route — the URL is the
     * context, here as everywhere else in this file, and nothing clears it by hand.
     */
    function onChooseOrganisation(organisationId: string): void {
      // 🔴 `choose` answers "was this allowed", not "did anything change" — it returns true for the
      // company already chosen. Without this, re-picking the current company threw the whole screen
      // away and reloaded it for nothing. Caught by the test that says so.
      const before = organisation.chosen?.id ?? null;
      if (!organisation.choose(organisationId)) return;
      if ((organisation.chosen?.id ?? null) === before) return;
      // Leave the production FIRST: a show belongs to the company making it, so the modules should
      // come back on a route that is this company's rather than reload and then be navigated away.
      if (inProduction.value && wayOut.value !== null)
        navigateToUrl(wayOut.value);
      void reloadMountedModules();
      closeNav();
    }

    function onSubClick(sub: NavBarSubItem): void {
      const samePath = path.value.split("?")[0] === sub.route.split("?")[0];
      navigateToUrl(sub.route);
      closeNav();
      if (samePath) {
        const [p, q] = sub.route.split("?");
        window.dispatchEvent(
          new CustomEvent("pinksy:navigate", {
            detail: {
              path: p,
              query: Object.fromEntries(new URLSearchParams(q || "")),
            },
          }),
        );
      }
    }

    return {
      t,
      // The companies she works at — never an agency that represents her (D164, `companies`).
      organisationChoices: computed(() => organisation.companies),
      // …and the agencies that represent her, drawn in their own colours when she acts for no company.
      representingAgencies: computed(() => organisation.agencies),
      chosenOrganisation: computed(() => organisation.chosen),
      organisationReady: computed(() => organisation.ready),
      onChooseOrganisation,
      navGroups,
      soleModule,
      homeEnabled,
      inProduction,
      standingLoading,
      nothingOpen,
      inModule,
      moduleGroups,
      moduleWayOut,
      onLeaveModule,
      wayOut,
      contextName,
      contextIcon,
      contextSubtitle,
      contextTitle,
      onLeaveProduction,
      mobile,
      navOpen,
      rail,
      onDrawerModel,
      expanded,
      hasItems,
      isActive,
      isSubActive,
      groupSubItems,
      onHomeClick,
      onItemClick,
      onSubClick,
      scrollEl,
      indicator,
      indicatorStyle,
      onExpandEnter,
      onExpandDone,
      onExpandLeave,
    };
  },
});
</script>

<style scoped>
/* Density, radii and hover strengths mirror legacy-client's SidebarV2.vue
   (`.sv2-*`) — see src/styles/standalone.css for the shared tokens. Sit BELOW
   the fixed TopBar rather than under it: Vuetify's own layout registration
   handles this inside THIS <v-app>, but it is forced explicitly so the drawer's
   position matches the SAME --pinksy-header-height contract the hosted
   single-spa modules read for their own padding-top, rather than depending on
   two different mechanisms staying in sync by accident. */
/* Between the bar and the rail, never under either: both heights are published
   contracts (App.vue), and the drawer subtracts BOTH so its own footer — the
   avatar, the locale switch, the theme toggle — is never half-covered by the
   rail drawn over it. */
.standalone-nav {
  top: var(--pinksy-header-height, 0px) !important;
  height: calc(
    100% - var(--pinksy-header-height, 0px) - var(--pinksy-rail-height, 0px)
  ) !important;
  background: var(--sh-chrome-bg) !important;
  border-right: 1px solid var(--sh-hairline) !important;
}
/* Overlay, not layout: on mobile the drawer floats above the module with a
   shadow instead of a shared border. Vuetify draws its own scrim (a sibling
   of this element, so it is not styled from here); a tap on it closes the
   drawer. */
.standalone-nav--mobile {
  border-right: 0 !important;
  box-shadow: none !important;
}
/* Only while it is OUT: closed, the shadow bled a ~36px grey smear along every phone screen's left edge. */
.standalone-nav--mobile.v-navigation-drawer--active {
  box-shadow: 0 20px 48px -12px rgba(0, 0, 0, 0.55) !important;
}

.sh-nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 8px;
  overflow: hidden;
}
.sh-nav--rail {
  padding: 10px 4px;
}

/* ── The way OUT of a production ───────────────────────────────────────
   A BOXED control, deliberately unlike every destination below it: those are
   places you can go, this is the way back to where places are chosen. Full
   width so it reads as a lid on the panel rather than as its first row. */
.sh-out {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 28px;
  margin-bottom: 8px;
  padding: 0 8px;
  border: 1px solid var(--sh-hairline);
  border-radius: var(--sh-radius);
  background: transparent;
  cursor: pointer;
  color: var(--sh-text-60);
  text-align: left;
  transition:
    background var(--sh-motion) ease,
    color var(--sh-motion) ease,
    border-color var(--sh-motion) ease;
}
.sh-nav--rail .sh-out {
  justify-content: center;
  padding: 0;
}
.sh-out:hover {
  background: var(--sh-hover);
  border-color: var(--sh-text-45);
  color: var(--sh-text);
}
.sh-out:active {
  transform: scale(0.985);
}
/* The arrow leads the way it is going. */
.sh-out__icon {
  flex: 0 0 auto;
  transition: transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-out:hover .sh-out__icon {
  transform: translateX(-2px);
}
.sh-out__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Whose production this is ──────────────────────────────────────────
   Icon, name, and a line that says what it is. The name is the loudest thing
   in the drawer on purpose: everything under it belongs to this production,
   and a person who has forgotten which show they opened must not have to
   read a route to find out. */
.sh-context {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 2px 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--sh-hairline);
}
.sh-nav--rail .sh-context {
  justify-content: center;
  padding: 2px 0 10px;
}
.sh-context__icon {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.12);
}
.sh-context__id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}
.sh-context__name {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Kind · territory · dates — what it IS, never a uuid.
   WRAPS rather than ellipsizes: at the drawer's 230px, "Production · ZA · from
   12 Mar 2026" is four characters too long, and an identity line that ends in
   "from 12 Mar 2…" has thrown away the one part a producer was reading it for.
   Two lines, then clipped — a subtitle is not a paragraph. */
.sh-context__sub {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--sh-text-60);
  overflow-wrap: anywhere;
}

/* ── Scroll area ───────────────────────────────────────────────────────── */
.sh-scroll {
  position: relative; /* the offsetParent the sliding marker is measured against */
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 -4px;
  padding: 0 4px;
}

/* ── Sliding active marker ─────────────────────────────────────────────
   A 3px brand bar with a soft glow, on the row's left edge. `top`/`height`
   are set inline by measureIndicator(); the transition is what makes a
   route change read as the marker TRAVELLING to the new row. */
.sh-indicator {
  position: absolute;
  left: 4px;
  width: 3px;
  border-radius: 3px;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 10px 1px rgba(var(--v-theme-primary), 0.55);
  pointer-events: none;
  z-index: 1;
  transition:
    top var(--sh-motion-slow) var(--sh-ease),
    height var(--sh-motion-slow) var(--sh-ease),
    opacity var(--sh-motion) ease;
}
.sh-nav--rail .sh-indicator {
  left: 0;
}

/* ── Nav rows ──────────────────────────────────────────────────────────── */
.sh-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  height: var(--sh-row-height);
  padding: 0 8px;
  margin: 1px 0;
  border-radius: var(--sh-radius);
  cursor: pointer;
  user-select: none;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
}
.sh-nav--rail .sh-item {
  justify-content: center;
  padding: 0;
  height: 34px;
}
.sh-item:hover {
  background: var(--sh-hover);
}
/* A press gives back a little: the row dips, the way a real key does. */
.sh-item:active {
  transform: scale(0.985);
}
.sh-item--active {
  background: var(--sh-active);
}
/* Rows of a freshly-drawn list arrive one after another, top to bottom
   (`--i` is the row's index, set inline). Plays on mount only — a route
   change that keeps the same rows does not replay it. */
.sh-item--in {
  /* `backwards`, not `both`: a forwards-filled `transform: none` would outrank
     the :active press-dip below for the row's whole life. */
  animation: sh-row-in var(--sh-motion-slow) var(--sh-ease) backwards;
  animation-delay: calc(var(--i, 0) * 24ms);
}
.sh-item__icon {
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--sh-text-60);
  transition:
    color var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-item:hover .sh-item__icon {
  color: var(--sh-text);
  transform: translateX(1px) scale(1.08);
}
.sh-item--active .sh-item__icon {
  color: rgb(var(--v-theme-primary));
  animation: sh-icon-pop var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-item__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color var(--sh-motion) ease;
  /* Labels are `v-if`-ed away in the rail, so they MOUNT when it expands —
     this is the rail-expand reveal, with no extra state. */
  animation: sh-label-in var(--sh-motion-slow) var(--sh-ease) both;
}
.sh-item--active .sh-item__label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}
.sh-item__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
  transition:
    transform var(--sh-motion-slow) var(--sh-ease-spring),
    color var(--sh-motion) ease;
}
.sh-item:hover .sh-item__chev {
  color: var(--sh-text) !important;
}
.sh-item__chev--open {
  transform: rotate(180deg);
}
/* Rail: a corner cue that the row hides more behind a flyout. */
.sh-item__more {
  position: absolute;
  right: 2px;
  bottom: 3px;
  color: var(--sh-text-45) !important;
}

/* ── Section eyebrow (single-module nav) ───────────────────────────────
   Same type and pitch as SidebarV2's `.sv2-section__header`: it labels the
   group BELOW it, so the gap sits above. */
.sh-section {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  margin-top: 5px;
  padding: 0 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
  user-select: none;
  animation: sh-label-in var(--sh-motion-slow) var(--sh-ease) both;
}
/* The eyebrow trails off into a hairline — a heading, not a floating word. */
.sh-section::after {
  content: "";
  flex: 1 1 auto;
  height: 1px;
  background: linear-gradient(90deg, var(--sh-hairline), transparent);
}
/* The rail has no room for the label, so the group boundary becomes a rule. */
.sh-section__rule {
  height: 1px;
  margin: 6px 8px;
  background: var(--sh-hairline);
}

/* D157 — the production's rail while its standing is in flight: muted bars at row height, no motion
   beyond a slow fade, so a boot reads as "arriving", not as a menu. */
.sh-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 12px;
}
.sh-skeleton__row {
  height: 10px;
  border-radius: 5px;
  background: var(--sh-hairline);
  width: calc(90% - (var(--row, 1) * 9%));
  animation: sh-skeleton-breathe 1.6s ease-in-out infinite;
}
@keyframes sh-skeleton-breathe {
  50% {
    opacity: 0.45;
  }
}
@media (prefers-reduced-motion: reduce) {
  .sh-skeleton__row {
    animation: none;
  }
}
.sh-nothing {
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--sh-text-45);
}

/* ── Sub-sections (accordion, multi-module nav) ────────────────────────
   Metrics mirror SidebarV2's `.sv2-children__label` / `.sv2-child`. */
.sh-children {
  padding: 1px 0 3px;
}
.sh-expand-enter-active,
.sh-expand-leave-active {
  transition:
    height 260ms var(--sh-ease),
    opacity var(--sh-motion) ease;
}
.sh-expand-enter-from,
.sh-expand-leave-to {
  opacity: 0;
}
.sh-children__label {
  padding: 4px 8px 2px 30px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--sh-text-45);
}
.sh-child {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  padding: 0 8px 0 30px;
  border-radius: var(--sh-radius);
  cursor: pointer;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
  animation: sh-row-in var(--sh-motion-slow) var(--sh-ease) backwards;
  animation-delay: calc(var(--i, 0) * 28ms);
}
.sh-child:hover {
  background: var(--sh-hover);
}
.sh-child:active {
  transform: scale(0.985);
}
.sh-child--active {
  background: var(--sh-active);
}
.sh-child__bullet {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
  transition:
    color var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-child:hover .sh-child__bullet {
  color: var(--sh-text) !important;
  transform: scale(1.15);
}
.sh-child--active .sh-child__bullet {
  color: rgb(var(--v-theme-primary)) !important;
}
.sh-child__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-child--active .sh-child__label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* ── Rail flyout ───────────────────────────────────────────────────────── */
.sh-flyout {
  min-width: 190px;
  padding: 6px;
  border-radius: 10px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 14px 34px -18px rgba(0, 0, 0, 0.6);
}
.sh-flyout__title {
  padding: 4px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.sh-flyout .sh-child {
  padding-left: 8px;
}

/* ── Motion ────────────────────────────────────────────────────────────
   Every keyframe here is namespaced `sh-` (module CSS is global on the same
   page). `prefers-reduced-motion` switches them all off in standalone.css. */
@keyframes sh-row-in {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sh-label-in {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sh-icon-pop {
  0% {
    transform: scale(0.8);
  }
  60% {
    transform: scale(1.18);
  }
  100% {
    transform: scale(1);
  }
}

/* ── Touch targets ─────────────────────────────────────────────────────
   LAST in the sheet on purpose: a media query does not raise specificity,
   so these must come after the desktop rules they override. `767.98px`, not
   `768px`, to match Vuetify's own `width < mobileBreakpoint` test exactly —
   at 768 the drawer is still permanent. The desktop rows are 30px, which is
   right for a mouse and under the 44px minimum for a finger. */
@media (max-width: 767.98px) {
  .sh-item {
    height: 44px;
  }
  .sh-item__label {
    font-size: 14px;
  }
  .sh-child {
    height: 40px;
    padding-left: 34px;
  }
  .sh-child__label {
    font-size: 13px;
  }
  .sh-section {
    height: 26px;
  }
}
</style>
